import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { requestNotificationPermission } from '../../utils/notifications';
import { ENABLE_NATIVE_MOBILE_PUSH } from './appConfig';

export async function queryPermissionState(name, fallback = 'prompt') {
  try {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      return fallback;
    }
    const result = await navigator.permissions.query({ name });
    return result?.state || fallback;
  } catch {
    return fallback;
  }
}

export async function refreshPermissionStatusFlow(setPermissionStatus) {
  let notificationState = 'unsupported';
  const nativePlatform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
  const isNativeMobile = nativePlatform === 'android' || nativePlatform === 'ios';

  if (isNativeMobile && ENABLE_NATIVE_MOBILE_PUSH && PushNotifications && typeof PushNotifications.checkPermissions === 'function') {
    const permissionCheck = await PushNotifications.checkPermissions().catch(() => ({ receive: 'prompt' }));
    notificationState = permissionCheck?.receive === 'granted'
      ? 'granted'
      : permissionCheck?.receive === 'denied'
        ? 'denied'
        : 'default';
  } else {
    notificationState = typeof Notification !== 'undefined' ? (Notification.permission || 'default') : 'unsupported';
  }

  const geolocationState = navigator.geolocation ? await queryPermissionState('geolocation', 'prompt') : 'unsupported';
  const microphoneState = (navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    ? await queryPermissionState('microphone', 'prompt')
    : 'unsupported';

  setPermissionStatus({
    notifications: notificationState,
    geolocation: geolocationState,
    microphone: microphoneState,
  });
}

export async function requestAppPermissionsFlow({
  requestingPermissions,
  setRequestingPermissions,
  notificationsEnabled,
  initializeNotifications,
  initializeNativeMobilePush,
  refreshPermissionStatus,
  showToast,
}) {
  if (requestingPermissions) return;
  setRequestingPermissions(true);

  try {
    let notificationGranted = false;
    const nativePlatform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
    const isNativeMobile = nativePlatform === 'android' || nativePlatform === 'ios';

    if (isNativeMobile) {
      try {
        notificationGranted = await requestNotificationPermission();
        if (notificationGranted && ENABLE_NATIVE_MOBILE_PUSH) {
          await initializeNativeMobilePush();
        }
      } catch {
        notificationGranted = false;
      }
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        notificationGranted = await requestNotificationPermission();
      } catch {
        notificationGranted = false;
      }
    } else {
      notificationGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
    }

    if (navigator.geolocation) {
      const geoState = await queryPermissionState('geolocation', 'prompt');
      if (geoState === 'prompt') {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(true),
            (error) => reject(error),
            { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
          );
        }).catch(() => false);
      }
    }

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const micState = await queryPermissionState('microphone', 'prompt');
      if (micState === 'prompt') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      }
    }

    await refreshPermissionStatus();
    if (notificationGranted && notificationsEnabled) {
      await initializeNotifications();
    }
    showToast('✅ Permissies bijgewerkt', 'success');
  } catch (error) {
    console.warn('Permission request flow failed:', error);
    showToast('⚠️ Niet alle permissies konden worden aangevraagd', 'warning');
  } finally {
    setRequestingPermissions(false);
  }
}
